/**
 * Phase display selectors for CUBK timeline card
 *
 * Two-source architecture:
 * - Step 1 (sync and categorization): derived from attention states in cubkProgress slice
 * - Step 2 (prepare for bookkeeping): derived from incomplete onboarding items
 * - Steps 3-4 (bookkeeping review, complete): derived from the `phase` field on
 *   cubkProgress (the polled /progress source of truth). The mount-time
 *   catchUpBookkeepingStatus slice is still consulted for `followupRequestedAt`
 *   in the BK_TEAM_REVIEW_AFTER_FOLLOWUP branch.
 *
 * Action Required state is driven by `followupRequestedAt` timestamp on catchUpBookkeepingStatus.
 * Rich followup alert content comes from `followupRequest` on cubkProgress (to be added to /progress endpoint).
 */
import { createSelector } from 'reselect'
import { FOLLOWUP_ITEMS } from './followupRoutes'
import { selectCatchUpBookkeepingStatusForCurrentUser } from '@/components/CatchUpBookkeepingStatus/catchUpBookkeepingStatus.selector'
import {
  selectCubkProgressData,
  selectCubkCardState,
  selectCubkProgress,
  buildTimelineSteps,
  type CubkTimelineStep,
  type CubkProgress,
} from './lib/cubkProgress.selectors'
import {
  AttentionState,
  CubkCardState,
  type FollowupRequest,
} from './lib/cubkProgress.types'
import {
  selectHasOpenExpediteCards,
  selectOpenExpediteCards,
  EXPEDITE_CARD_IDS,
} from '../cubk-deadline'
import { getUserNeedsClarificationTransactionsSelector } from '@/features/transactions/transactions.selectors'
import {
  GettingStartedOnboardingStep,
  OnboardingStepPhase,
} from '@/features/onboarding/user-onboarding-steps/onboarding.reducer'
import {
  getUserOnboardingSteps,
  getAllOnboardingSteps,
} from '@/features/onboarding/user-onboarding-steps/onboarding.selectors'
import {
  BankingDisplayState,
  selectBankingDisplayState,
  selectHasConnectedBankAccount,
} from '@/features/banking/banking.selectors'
import {
  BLOCKING_ITEM_ROUTES,
  type BlockingItemRoute,
} from './blockingItemRoutes'

/**
 * Determines the display phase for the CUBK card.
 *
 * Priority:
 * 0. Banking application gate (selectBankingDisplayState) — wins over
 *    everything below only when the user has no connected bank accounts.
 * 1. Attention state overrides from cubkProgress (bank sync, categorization, errors)
 * 2. Backend `phase` field from catchUpBookkeepingStatus slice. bk_review is
 *    downgraded to PREPARE_FOR_BOOKKEEPING when expedite-card items are open.
 * 3. PREPARE_FOR_BOOKKEEPING when user has open expedite-card items and no
 *    backend phase has been set yet.
 * 4. Fallback: existing attention state logic via selectCubkCardState
 *
 * The "Prepare for bookkeeping" gate uses `selectHasOpenExpediteCards` from the
 * cubk-deadline feature so that the urgent-deadline banner and the stepper
 * agree on which items block the bookkeeper. Adding a new bookkeeping-blocking
 * step means updating EXPEDITE_CARD_IDS in one place.
 */
export const selectCubkDisplayPhase = createSelector(
  selectBankingDisplayState,
  selectHasConnectedBankAccount,
  selectCubkProgressData,
  selectCatchUpBookkeepingStatusForCurrentUser,
  selectCubkCardState,
  selectHasOpenExpediteCards,
  (
    bankingDisplayState,
    hasAnyBank,
    progressData,
    cubkStatus,
    fallbackState,
    hasOpenExpediteCards
  ): CubkCardState => {
    // Step 0: Banking application gate — skip when user has any connected
    // FI so CUBK progress is visible even while Heard Banking is pending or
    // the user hasn't connected Heard bank specifically.
    if (!hasAnyBank) {
      switch (bankingDisplayState) {
        case BankingDisplayState.AwaitingApplication:
          return CubkCardState.BANKING_AWAITING_APPLICATION
        case BankingDisplayState.ApprovedNoAccounts:
          return CubkCardState.BANKING_APPROVED_NO_ACCOUNTS
        case BankingDisplayState.NoAccountsFallback:
          return CubkCardState.BANKING_NO_ACCOUNTS_FALLBACK
        case BankingDisplayState.Normal:
          break
      }
    }

    // Step 1: Check attention states for early-phase overrides (step 1)
    if (progressData) {
      const { attentionStates } = progressData

      // Error states take highest priority
      if (
        attentionStates.includes(AttentionState.INSTITUTION_UNSUPPORTED) ||
        attentionStates.includes(AttentionState.BANK_SYNC_ERROR_RETRYABLE) ||
        attentionStates.includes(AttentionState.BANK_REAUTH_REQUIRED)
      ) {
        return CubkCardState.BANK_INCOMPATIBLE
      }

      // Active bank sync or categorization (step 1 — combined)
      if (
        attentionStates.includes(AttentionState.BANK_SYNC_IN_PROGRESS) ||
        attentionStates.includes(AttentionState.CATEGORIZATION_IN_PROGRESS)
      ) {
        return CubkCardState.SYNC_AND_CATEGORIZATION
      }
    }

    // Step 2: phase from polled cubkProgress only — CubkCard gates rendering
    // on /progress resolving, so the mount-time cubkStatus fallback is unsafe.
    const backendPhase = progressData?.phase
    if (backendPhase === 'complete') return CubkCardState.COMPLETE
    if (backendPhase === 'your_review') return CubkCardState.YOUR_REVIEW
    if (backendPhase === 'bk_review') {
      // Open expedite cards block bookkeeper review — keep step 1 InProgress.
      if (hasOpenExpediteCards) {
        return CubkCardState.PREPARE_FOR_BOOKKEEPING
      }

      // Distinguish "first-time bk_review" from "bk_review after the user
      // finished a followup round". Signal: followupRequestedAt has been set
      // at some point AND the live progressData reports no active followup.
      // In that case step 4 (Your review) should render Complete.
      const hasHadFollowup = Boolean(cubkStatus?.followupRequestedAt)
      const followupActive = progressData?.followupRequest?.isActive === true
      if (hasHadFollowup && !followupActive) {
        return CubkCardState.BK_TEAM_REVIEW_AFTER_FOLLOWUP
      }
      return CubkCardState.BK_TEAM_REVIEW
    }
    if (backendPhase === 'bank_incompatible')
      return CubkCardState.BANK_INCOMPATIBLE

    // Step 3: No backend phase — check if user has incomplete bookkeeping items.
    // This is the "Prepare for bookkeeping" state: sync is done but user
    // has outstanding action items before the bookkeeper can start.
    // Backend phase takes precedence (Step 2 above) — if bk_review/your_review/complete
    // are set, we never reach here regardless of onboarding item state.
    //
    // Gated on progressData being loaded: the onboarding slice is preloaded by
    // the parent Dashboard before CubkCard mounts, so without this guard the
    // selector returns PREPARE_FOR_BOOKKEEPING on first render (timeline shows
    // step 1 Complete) and then flips backwards to SYNC_AND_CATEGORIZATION
    // (step 1 InProgress) once /progress responds with BANK_SYNC_IN_PROGRESS.
    if (progressData && hasOpenExpediteCards) {
      return CubkCardState.PREPARE_FOR_BOOKKEEPING
    }

    // Step 4: Fallback to existing attention state logic
    return fallbackState
  }
)

/**
 * Whether the user has an active action required state.
 *
 * Polled cubkProgress is the live source of truth: when it's loaded, its
 * followupRequest field reflects the current state (including absence after
 * the user finishes their followup). cubkStatus is fetched once at mount and
 * goes stale, so it's only used as a fallback for the first render.
 *
 * Fallback safety: followupRequestedAt is a write-once DB flag — it means
 * "was ever triggered", not "is currently active". Using it as a fallback is
 * only valid when phase is your_review (or undefined, for backwards
 * compatibility before phase was added). When phase is bk_review, the user
 * has completed their tasks and action required must be false.
 */
export const selectHasActionRequired = createSelector(
  selectCatchUpBookkeepingStatusForCurrentUser,
  selectCubkProgressData,
  (cubkStatus, progressData): boolean => {
    if (progressData) {
      return progressData.followupRequest?.isActive === true
    }
    // Fallback: progressData not yet loaded (first render before poll fires).
    // Only use followupRequestedAt when phase is not bk_review — in bk_review
    // the followup round is over regardless of what followupRequestedAt says.
    if (cubkStatus?.phase === 'bk_review') {
      return false
    }
    return Boolean(cubkStatus?.followupRequestedAt)
  }
)

/**
 * Followup alert data for the contextual yellow alert in expanded state.
 * Returns null when no followup is active.
 *
 * Reads from cubkProgress `followupRequest` field (rich data from backend).
 */
export const selectFollowupAlertData = createSelector(
  selectCubkProgressData,
  (data): FollowupRequest | null => data?.followupRequest ?? null
)

/**
 * Timeline steps driven by the display phase (which incorporates the backend
 * phase field), not just the progress-based fallback. This ensures the
 * timeline updates correctly for states like COMPLETE and YOUR_REVIEW that
 * come from the backend rather than attention states.
 */
export const selectDisplayPhaseTimelineSteps = createSelector(
  selectCubkDisplayPhase,
  (displayPhase): CubkTimelineStep[] => buildTimelineSteps(displayPhase)
)

/**
 * Returns the top incomplete followup action item enriched with its frontend route.
 * The backend orders affectedItems by FOLLOWUP_STEP_IDS, so the first item is always
 * the highest-priority incomplete item. Returns null when no followup is active.
 */
export const selectTopFollowupItem = createSelector(
  selectFollowupAlertData,
  (followup): { id: string; label: string; route: string } | null => {
    if (!followup?.isActive || !followup.affectedItems.length) return null
    const topId = followup.affectedItems[0]
    const item = FOLLOWUP_ITEMS[topId]
    if (!item) return null
    return { id: topId, label: item.label, route: item.route }
  }
)

/**
 * Wraps selectCubkProgress to display 100% when CUBK is COMPLETE.
 * (selectCubkProgress derives from selectCubkCardState, which never returns COMPLETE.)
 */
export const selectCubkProgressForDisplay = createSelector(
  selectCubkProgress,
  selectCubkDisplayPhase,
  (progress, displayPhase): CubkProgress => {
    if (displayPhase === CubkCardState.COMPLETE) {
      return { ...progress, percentage: 100 }
    }
    return progress
  }
)

/**
 * Incomplete BK-blocking onboarding items for the warning banner, ordered by
 * EXPEDITE_CARD_IDS priority.
 *
 * CLARIFY_UNCATEGORIZED_TRANSACTIONS has two guards:
 * 1. Excluded when 0 transactions need clarification (prevents badge escalation
 *    with an empty banner).
 * 2. Excluded unless a followup is currently active — only surfaces after the
 *    bookkeeper explicitly triggers Request CUBK Followup. Uses progressData
 *    (polled, live) when available; falls back to phase-aware cubkStatus check
 *    on first render. followupRequestedAt alone is not sufficient — it is a
 *    write-once flag that remains set after round 1 completes.
 */
export const selectBlockingItemsAlertData = createSelector(
  selectOpenExpediteCards,
  getUserNeedsClarificationTransactionsSelector,
  selectCatchUpBookkeepingStatusForCurrentUser,
  selectCubkProgressData,
  (
    openCardIds,
    needsClarificationTxns,
    cubkStatus,
    progressData
  ): BlockingItemRoute[] => {
    // Determine if a followup round is currently active. progressData is the
    // live source; fall back to phase-aware cubkStatus on first render.
    const isFollowupActive = progressData
      ? progressData.followupRequest?.isActive === true
      : cubkStatus?.phase !== 'bk_review' &&
        Boolean(cubkStatus?.followupRequestedAt)

    return EXPEDITE_CARD_IDS.filter((id) => openCardIds.includes(id))
      .filter((id) => {
        if (
          id === GettingStartedOnboardingStep.clarifyUncategorizedTransactions
        ) {
          // Only surface CLARIFY while a followup round is active.
          return needsClarificationTxns.length > 0 && isFollowupActive
        }
        return true
      })
      .map((id): BlockingItemRoute | null => {
        const routeConfig = BLOCKING_ITEM_ROUTES[id]
        if (!routeConfig) return null
        return { id, ...routeConfig }
      })
      .filter((item): item is BlockingItemRoute => item !== null)
  }
)

/** True when ≥1 visible blocking item exists. Derived from selectBlockingItemsAlertData so badge and banner stay in sync. */
export const selectHasIncompleteBlockingItems = createSelector(
  selectBlockingItemsAlertData,
  (items): boolean => items.length > 0
)

/** True when CUBK is complete and GOW is the sole remaining incomplete getting-started step. */
export const selectIsGOWFinalDecision = createSelector(
  selectCubkProgressData,
  getUserOnboardingSteps,
  getAllOnboardingSteps,
  (progressData, userOnboardingSteps, allOnboardingSteps): boolean => {
    if (progressData?.phase !== 'complete') return false

    const gettingStartedStepIds = Object.values(allOnboardingSteps)
      .filter((step) => step.phase === OnboardingStepPhase.gettingStarted)
      .map((step) => step.identifier)

    const userGettingStartedSteps = Object.values(userOnboardingSteps).filter(
      (userStep) => gettingStartedStepIds.includes(userStep.onboardingStepId)
    )

    const gowStep = userGettingStartedSteps.find(
      (s) =>
        s.onboardingStepId ===
        GettingStartedOnboardingStep.joinGroupOnboardingSession
    )

    if (!gowStep) return false
    if (gowStep.completedAt || gowStep.skippedAt) return false

    const otherSteps = userGettingStartedSteps.filter(
      (s) =>
        s.onboardingStepId !==
        GettingStartedOnboardingStep.joinGroupOnboardingSession
    )

    return otherSteps.every((s) => s.completedAt || s.skippedAt)
  }
)

export const selectGOWStep = createSelector(
  getUserOnboardingSteps,
  (userOnboardingSteps) =>
    Object.values(userOnboardingSteps).find(
      (s) =>
        s.onboardingStepId ===
        GettingStartedOnboardingStep.joinGroupOnboardingSession
    ) ?? null
)
